import { useAppSelector } from "@/v2/hooks/hooks";
import { Fragment, ReactNode } from "react";
import { User } from "@/v2/types/User";
import { ModuleKey } from "./modules";
import { PermissionMethod } from "./permissions";

interface AccessControlProps {
    children: ReactNode;
    module: ModuleKey;
    requestedAccess: PermissionMethod | PermissionMethod[];
}

export default function AccessControl({
    module,
    requestedAccess,
    children,
}: AccessControlProps): ReactNode {
    const user = useAppSelector((state) => state.user.value) as User | null;

    if (!user) return null;

    if (user.is_super_admin) {
        return <Fragment>{children}</Fragment>;
    }

    if (user.is_admin || user?.roles?.some((role) => role.is_admin)) {
        return <Fragment>{children}</Fragment>;
    }

    const requestedMethods = Array.isArray(requestedAccess)
        ? requestedAccess
        : [requestedAccess];

    if (user?.roles) {
        for (const role of user?.roles) {
            if (role?.module_permissions) {
                for (const permission of role.module_permissions) {
                    if (permission.module.handle !== module) continue;

                    // Full access to module
                    if (permission.full_access_to_module) {
                        return <Fragment>{children}</Fragment>;
                    }

                    // Check if all requested methods are available
                    if (
                        requestedMethods.every((method) =>
                            permission?.method?.includes(method),
                        )
                    ) {
                        return <Fragment>{children}</Fragment>;
                    }
                }
            }
        }
    }

    return null;
}
